import { findIndex } from "lodash";
import { create } from "zustand";
import { devtools, persist } from "zustand/middleware";
import { immer } from "zustand/middleware/immer";
import { Entity } from "../../../common/models/entity.model";
import { FLOW_TYPES, STEPS } from "../constants";
import { Address, SpacePurpose, TimeZone } from "../models";
import { AddressKey, FlowType, LocationType, Step } from "../types";

type State = {
    flowType: FlowType;
    isOpen: boolean;
    step: Step;
    company: string;
    location: string;
    locationType?: LocationType;
    address: Address;
    locationTimeZone?: TimeZone;
    spaces: { name: string; externalId?: string }[];
    spacePurposes: SpacePurpose[];
    newSpacePurposeOptions: string[];
    newSubSpacePurposeOptions: string[];
};

type Action = {
    setFlowType: (value: FlowType) => void;
    toggleVisibility: () => void;
    setStep: (newStep: Step) => void;
    setCompany: (value: string) => void;
    setLocation: (value: string) => void;
    setLocationType: (value: LocationType) => void;
    setAddress: (prop: AddressKey, value: string) => void;
    setLocationTimeZone: (value: TimeZone) => void;
    addSpace: (value: string) => void;
    setSpaces: (value: { name: string; externalId?: string }[]) => void;
    removeSpace: (index: number) => void;
    addNewSpacePurposeOption: (value: string) => void;
    addNewSubSpacePurposeOption: (value: string) => void;
    addSpacePurpose: (value: SpacePurpose) => void;
    removeSpacePurpose: (value: SpacePurpose) => void;
    removeSubSpacePurposeFromSpacePurpose: (
        spacePurpose: SpacePurpose,
        subSpacePurpose: Entity
    ) => void;
    addSubSpacePurposeToSpacePurpose: (
        spacePurpose: SpacePurpose,
        subSpacePurpose: Entity
    ) => void;
    setSpacesForSpacePurpose: (
        spacePurpose: SpacePurpose,
        space: { name: string; externalId?: string }[]
    ) => void;
    reset: () => void;
};

const initialState: State = {
    flowType: FLOW_TYPES.INITIAL,
    isOpen: false,
    step: STEPS.SET_COMPANY,
    company: "",
    location: "",
    locationType: undefined,
    locationTimeZone: undefined,
    address: {
        street: "",
        number: "",
        zipCode: undefined,
        city: "",
        country: "",
    },
    spaces: [],
    spacePurposes: [],
    newSpacePurposeOptions: [],
    newSubSpacePurposeOptions: [],
};

export const useSetUpSpacesStore = create(
    persist(
        devtools(
            immer<State & Action>((set) => ({
                ...initialState,

                setFlowType: (value) => set({ flowType: value }),
                toggleVisibility: () =>
                    set((state) => ({ isOpen: !state.isOpen })),
                setStep: (value) => set({ step: value }),
                setCompany: (value) => set({ company: value }),
                setLocation: (value) => set({ location: value }),
                setLocationType: (value) => set({ locationType: value }),
                setAddress: (prop, value) =>
                    set((state) => {
                        state.address[prop] = value;
                    }),
                setLocationTimeZone: (value) =>
                    set({ locationTimeZone: value }),
                addSpace: (value) =>
                    set((state) => {
                        state.spaces.push({ name: value });
                    }),
                setSpaces: (value) =>
                    set({
                        spaces: value,
                    }),
                removeSpace: (index) =>
                    set((state) => {
                        state.spaces.splice(index, 1);
                        // Might need to remove it also from spacePurposes (take in name instead of index)
                    }),
                addNewSpacePurposeOption: (value) =>
                    set((state) => {
                        state.newSpacePurposeOptions.push(value);
                    }),
                addNewSubSpacePurposeOption: (value) =>
                    set((state) => {
                        state.newSubSpacePurposeOptions.push(value);
                    }),
                addSpacePurpose: (value) =>
                    set((state) => {
                        state.spacePurposes.push(value);
                    }),
                removeSpacePurpose: (value) =>
                    set((state) => {
                        const index = findIndex(state.spacePurposes, {
                            name: value.name,
                        });
                        state.spacePurposes.splice(index, 1);
                    }),
                removeSubSpacePurposeFromSpacePurpose: (
                    spacePurpose,
                    subSpacePurpose
                ) =>
                    set((state) => {
                        const spacePurposeIndex = state.spacePurposes.findIndex(
                            (value) => value.name === spacePurpose.name
                        );
                        if (spacePurposeIndex < 0) {
                            return;
                        }
                        const subSpacePurposeIndex = state.spacePurposes[
                            spacePurposeIndex
                        ].subSpacePurposes.findIndex(
                            (value) => value.name === subSpacePurpose.name
                        );
                        state.spacePurposes[
                            spacePurposeIndex
                        ].subSpacePurposes.splice(subSpacePurposeIndex, 1);
                    }),
                addSubSpacePurposeToSpacePurpose: (
                    spacePurpose,
                    subSpacePurpose
                ) =>
                    set((state) => {
                        const spacePurposeIndex = state.spacePurposes.findIndex(
                            (value) => value.name === spacePurpose.name
                        );
                        if (spacePurposeIndex < 0) {
                            return;
                        }
                        state.spacePurposes[
                            spacePurposeIndex
                        ].subSpacePurposes.push(subSpacePurpose);
                    }),
                setSpacesForSpacePurpose: (spacePurpose, spaces) =>
                    set((state) => {
                        const spacePurposeIndex = state.spacePurposes.findIndex(
                            (value) => value.name === spacePurpose.name
                        );
                        if (spacePurposeIndex < 0) {
                            return;
                        }
                        state.spacePurposes[spacePurposeIndex].spaces = spaces;
                    }),
                reset: () => set(() => initialState),
            }))
        ),
        {
            name: "spaces-setup-flow",
        }
    )
);
